import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Palette, QrCode, Save, Upload, Bot } from 'lucide-react';
import useStore, { BRAND_COLORS } from '@/store/useStore';
import { apiMerchantTheme, apiMerchantUpdateProfile } from '@/services/api';
import { getMerchantPrefs, setMerchantPrefs } from '@/lib/merchantPrefs';
import QrCodeModal from '@/components/merchant/QrCodeModal';
import { useUiLanguage } from '@/lib/i18n';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';

export default function SettingsPage() {
  const { t } = useUiLanguage();
  const { merchantBrandColor, merchantUser, setMerchantBrandColor, setMerchantUser } = useStore();
  const merchantId = merchantUser?.id || 'default';
  const selectableBrandColors = Object.entries(BRAND_COLORS).filter(([key]) => key !== 'black');
  const [showQR, setShowQR] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [location, setLocation] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoBase64, setLogoBase64] = useState('');
  const [aiName, setAiName] = useState('Nova');
  const [savingProfile, setSavingProfile] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('info');
  const fileRef = useRef(null);

  const showToast = (message, type = 'info') => {
    setToast(message);
    setToastType(type);
    window.setTimeout(() => setToast(''), 3800);
  };

  useEffect(() => {
    setProfileName(merchantUser?.name || '');
    setProfileEmail(merchantUser?.email || '');
    setRestaurantName(merchantUser?.restaurant_name || '');
    setLocation(merchantUser?.location || '');
    setLogoPreview(merchantUser?.logo_base64 || '');
    setLogoBase64(merchantUser?.logo_base64 || '');
    const prefs = getMerchantPrefs(merchantId);
    setAiName(prefs.ai_name || merchantUser?.name || 'Flyback Advisor');
  }, [merchantId, merchantUser]);

  const handleColorChange = async (color) => {
    setMerchantBrandColor(color);
    if (merchantUser) setMerchantUser({ ...merchantUser, brand_color: color });
    try { await apiMerchantTheme(color, null); } catch (e) {}
  };

  const handleProfileFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast(t('Logo must be under 5MB'), 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = String(ev.target?.result || '');
      setLogoPreview(dataUrl);
      setLogoBase64(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!restaurantName.trim() || !profileName.trim() || !profileEmail.trim()) {
      showToast(t('Name, email, and restaurant name are required.'), 'error');
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {
        name: profileName.trim(),
        email: profileEmail.trim().toLowerCase(),
        restaurant_name: restaurantName.trim(),
        location: location.trim(),
        logo_base64: logoBase64 || '',
      };
      const res = await apiMerchantUpdateProfile(payload);
      if (res?.merchant) setMerchantUser(res.merchant);
      setMerchantPrefs(merchantId, { ai_name: aiName.trim() || merchantUser?.name || 'Flyback Advisor' });
      showToast(t('Settings updated successfully.'), 'success');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : t('Failed to update settings.'), 'error');
    }
    setSavingProfile(false);
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <CenteredStatusPopup message={toast} type={toastType} />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Settings')}</h1>
        <p className="text-sm text-stone-500">{t('Manage profile, theme and QR setup.')}</p>
      </motion.div>

      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSave} className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t('Owner Name')}</label>
            <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} className="h-10 rounded-xl" data-testid="settings-owner-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t('Email')}</label>
            <Input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} className="h-10 rounded-xl" data-testid="settings-owner-email-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t('Restaurant Name')}</label>
            <Input value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} className="h-10 rounded-xl" data-testid="settings-restaurant-name-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t('Location')}</label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="h-10 rounded-xl" data-testid="settings-location-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.4fr,1fr,1fr] gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{t('Restaurant Logo')}</label>
            <p className="text-xs text-stone-500">{t('Suggested format: PNG (square logo works best).')}</p>
            <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleProfileFileChange} data-testid="settings-logo-input" />
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold flex items-center gap-2">
                <Upload className="w-4 h-4" /> {t('Upload Logo')}
              </button>
              {logoPreview && <img src={logoPreview} alt="Restaurant logo" className="w-10 h-10 rounded-lg object-cover border border-stone-200" />}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> {t('Theme Selection')}</label>
            <div className="flex flex-wrap gap-2">
              {selectableBrandColors.map(([key, color]) => (
                <button key={key} type="button" onClick={() => handleColorChange(key)} className={`w-8 h-8 rounded-lg border ${(merchantBrandColor === key || (merchantBrandColor === 'black' && key === 'brand')) ? 'ring-2 ring-offset-2' : ''}`} style={{ backgroundColor: color.hex, '--tw-ring-color': color.hex }} title={color.name} data-testid={`brand-color-${key}`} />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> {t('Store QR')}</label>
            <button type="button" onClick={() => setShowQR(true)} className="h-10 px-4 rounded-xl gradient-brand-btn text-sm font-semibold w-full" data-testid="settings-show-qr-btn">
              {t('Show QR')}
            </button>
          </div>
        </div>

        {/* <div className="rounded-xl border border-stone-200 p-3 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2"><Bot className="w-4 h-4 text-brand" /> Business Assistant</p>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-stone-500">Assistant Name</label>
            <Input value={aiName} onChange={(e) => setAiName(e.target.value)} placeholder={merchantUser?.name || 'Owner Name'} className="h-10 rounded-xl" />
          </div>
        </div> */}

        <button type="submit" disabled={savingProfile} className="h-10 px-4 rounded-xl gradient-brand-btn text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="settings-save-profile-btn">
          <Save className="w-4 h-4" /> {savingProfile ? t('Saving...') : t('Save Settings')}
        </button>
      </motion.form>

      <QrCodeModal open={showQR} onClose={() => setShowQR(false)} />
    </div>
  );
}
